import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./use-cases.css";
import { fetchArticles } from "./articlesApi";
import { useLanguage } from "../../i18n/LanguageContext";

const UseCases = () => {
  const { tr, lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleArticles, setVisibleArticles] = useState(9);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadArticles = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await fetchArticles();
        if (isMounted) {
          setArticles(data);
        }
      } catch (err) {
        console.error("Failed to load articles:", err);
        if (isMounted) {
          setLoadError(
            tr(
              "Failed to load articles. Please try again later.",
              "Gagal memuat artikel. Silakan coba lagi nanti."
            )
          );
          setArticles([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticles();
    return () => {
      isMounted = false;
    };
  }, [tr]);

  // Sort articles by date (newest first) and get featured articles
  const sortedArticles = [...articles].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA;
  });

  const featuredArticles = sortedArticles.slice(0, 5); // Top 5 newest articles
  const currentFeatured = featuredArticles[currentFeaturedIndex];

  const handleNextFeatured = () => {
    setCurrentFeaturedIndex((prev) => 
      prev === featuredArticles.length - 1 ? 0 : prev + 1
    );
  };

  const getArticleTitle = (article) => tr(article.title, article.titleId || article.title);
  const getArticleDescription = (article) => tr(article.description, article.descriptionId || article.description);
  const getArticleCategory = (category) => {
    const map = {
      Business: tr("Business", "Bisnis"),
      Product: tr("Product", "Produk"),
    };
    return map[category] || category;
  };
  const formatDate = (dateStr) => {
    if (lang !== "id") return dateStr;
    const months = {
      January: "Januari",
      February: "Februari",
      March: "Maret",
      April: "April",
      May: "Mei",
      June: "Juni",
      July: "Juli",
      August: "Agustus",
      September: "September",
      October: "Oktober",
      November: "November",
      December: "Desember",
    };
    return dateStr.replace(
      /(January|February|March|April|May|June|July|August|September|October|November|December)/,
      (match) => months[match] || match
    );
  };

  const filteredArticles = articles.filter((article) => {
    const matchesFilter = activeFilter === "All" || article.category === activeFilter;
    const title = (lang === "id" ? article.titleId : null) || article.title;
    const description = (lang === "id" ? article.descriptionId : null) || article.description;
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Display all filtered articles (including featured)
  const displayedArticles = filteredArticles
    .slice(0, visibleArticles);
  
  const hasMore = filteredArticles.length > visibleArticles;

  const handleLoadMore = () => {
    setVisibleArticles(prev => prev + 9);
  };

  const categoryValues = Array.from(
    new Set(articles.map((article) => article.category).filter(Boolean))
  );
  const categories = [
    { value: "All", label: tr("All", "Semua") },
    ...categoryValues.map((category) => ({
      value: category,
      label: getArticleCategory(category),
    })),
  ];

  return (
    <div className="use-cases-page">
      <Breadcrumbs />
      {/* Featured Article Carousel */}
      <div className="use-cases-featured-wrapper">
        {isLoading ? (
          <div className="use-cases-loading">
            {tr("Loading...", "Memuat...")}
          </div>
        ) : currentFeatured ? (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentFeaturedIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Link
                  to={`/use-cases/${currentFeatured.id}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <motion.section
                    className="use-cases-featured"
                    style={{ cursor: "pointer" }}
                  >
                    <div className="use-cases-featured-image">
                      <img
                        src={currentFeatured.image}
                        alt={currentFeatured.title}
                      />
                    </div>
                    <div className="use-cases-featured-content">
                      <span className="use-cases-featured-category">
                        {getArticleCategory(currentFeatured.category)}
                      </span>
                      <h1 className="use-cases-featured-title">
                        {getArticleTitle(currentFeatured)}
                      </h1>
                      <p className="use-cases-featured-description">
                        {getArticleDescription(currentFeatured)}
                      </p>
                      <span className="use-cases-featured-date">
                        {formatDate(currentFeatured.date)}
                      </span>
                    </div>
                  </motion.section>
                </Link>
              </motion.div>
            </AnimatePresence>

            {/* Arrow Button */}
            {featuredArticles.length > 1 && (
              <button
                className="use-cases-featured-arrow"
                onClick={handleNextFeatured}
                aria-label={tr(
                  "Next featured article",
                  "Artikel unggulan berikutnya"
                )}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </>
        ) : (
          <div className="use-cases-loading">
            {loadError || tr("No articles found.", "Artikel belum tersedia.")}
          </div>
        )}
      </div>

      {/* Navigation and Search */}
      <section className="use-cases-nav">
        <div className="use-cases-nav-container">
          <div className="use-cases-filters">
            {categories.map((category) => (
              <button
                key={category.value}
                className={`use-cases-filter-btn ${activeFilter === category.value ? "active" : ""}`}
                onClick={() => {
                  setActiveFilter(category.value);
                  setVisibleArticles(9);
                }}
              >
                {category.label}
              </button>
            ))}
          </div>
          <div className="use-cases-search">
            <input
              type="text"
              placeholder={tr("Search blog posts", "Cari artikel")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setVisibleArticles(9);
              }}
              className="use-cases-search-input"
            />
            <button className="use-cases-search-btn">{tr("Search", "Cari")}</button>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="use-cases-articles">
        <div className="use-cases-articles-grid">
          {displayedArticles.length === 0 && !isLoading ? (
            <div className="use-cases-loading">
              {loadError || tr("No articles found.", "Artikel belum tersedia.")}
            </div>
          ) : (
            displayedArticles.map((article, index) => (
              <Link
                key={article.id}
                to={`/use-cases/${article.id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <motion.article
                  className="use-cases-article-card"
                  initial={{ opacity: 0, y: 30 }}
                  {...(isMobile
                    ? {
                        animate: { opacity: 1, y: 0 },
                        transition: { delay: index * 0.03, duration: 0.3 },
                        whileHover: { y: -4 },
                      }
                    : {
                        whileInView: { opacity: 1, y: 0 },
                        transition: { delay: index * 0.1, duration: 0.5 },
                        viewport: { once: true },
                        whileHover: { y: -4 },
                      })}
                >
                  <div className="use-cases-article-image">
                    <img src={article.image} alt={article.title} />
                  </div>
                  <div className="use-cases-article-content">
                    <span className="use-cases-article-category">
                      {getArticleCategory(article.category)}
                    </span>
                    <h2 className="use-cases-article-title">
                      {getArticleTitle(article)}
                    </h2>
                    <p className="use-cases-article-description">
                      {getArticleDescription(article)}
                    </p>
                    <span className="use-cases-article-date">
                      {formatDate(article.date)}
                    </span>
                  </div>
                </motion.article>
              </Link>
            ))
          )}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <motion.div
            className="use-cases-load-more"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <motion.button
              className="use-cases-load-more-btn"
              onClick={handleLoadMore}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tr("Load more", "Muat lebih banyak")}
            </motion.button>
          </motion.div>
        )}
      </section>
    </div>
  );
};

export default UseCases;

